// Path: src/components/auth/session/WithAuthentication.tsx
// Updated to allow navigation to /home/profile for verified users
// Ensures redirects for unverified users to /verification
// Maintains public access to /OLSStudentProspect, /olsupdates/*, and other public routes
// Simplified redirect logic and improved logging
// Updated to normalize verified state for consistent redirect logic
// 5/24/25 12:00 PM
// Updated to improve verification check timing
// Added detailed redirect logging
// Updated verification logic to allow access to /home/profile and /home/referral

// Updated to prevent redirects for public routes like /referral
// Enhanced logging and ensured public routes like /referral skip redirects
// Fixed redirect loop by ensuring redirect executes only once
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../stores/UserStore';
import { useFirebase } from '../../dev/Firebase/Firebase';
import { Spin } from 'antd';
import { UserRole } from '../../../constants/UserEnums';

interface Props {
  children: React.ReactNode;
  authCondition?: (authUser: any) => boolean;
}

const WithAuthentication: React.FC<Props> = ({ children, authCondition }) => {
  const { auth } = useFirebase();
  const userStore = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isInitialSyncDone, setIsInitialSyncDone] = useState(false);
  const [redirectHistory, setRedirectHistory] = useState<string[]>([]);
  const hasEvaluatedRef = useRef(false); // Track if redirect has been evaluated

  const isPublicPath = useMemo(() => {
    const publicPaths = [
      '/login', '/signup', '/privacydisclosure', '/license', '/public-use',
      '/register', '/passwordreset', '/signupconfirmation', '/Contact',
      '/OLSStudentProspect', '/privacy', '/olsdataprovision', '/Stat',
      '/ah', '/aa', '/about-aa', '/rwh', '/about-rwh', '/OLSsPrivacy',
      '/OLSLMprivacy', '/OlsmePrivacy', '/estate', '/olsme', '/olsmeSub',
      '/aOLSLMasset', '/lightScience', '/venus', '/OLSaeo', '/OLShealth',
      '/OLSinventions', '/OLSCalendarTime', '/referral',
    ];
    const normalizedPath = location.pathname.toLowerCase();
    const isPublic = publicPaths.includes(normalizedPath) ||
      normalizedPath.includes('/reset') ||
      normalizedPath.includes('/olsupdates') ||
      normalizedPath.includes('/estate/');
    console.log('isPublicPath check:', { pathname: location.pathname, normalizedPath, isPublic });
    return isPublic;
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        userStore.setUser(
          {
            uid: user.uid,
            emailVerified: user.emailVerified,
          },
          userStore.userRole || UserRole.UNAUTHORIZED,
          user.emailVerified || false,
          userStore.user?.submittedForVerification || false,
          userStore.user?.verified || false
        );
        console.log('WithAuthentication: User authenticated - UID:', user.uid, 'emailVerified:', user.emailVerified, 'verified:', userStore.verified);
        setIsInitialSyncDone(true);
      } else {
        userStore.clearUser();
        console.log('WithAuthentication: No user authenticated');
        setIsInitialSyncDone(true);
      }
    });

    return () => unsubscribe();
  }, [auth, userStore]);

  const handleRedirect = (path: string, reason: string) => {
    console.log(`WithAuthentication: Redirecting to ${path} - Reason: ${reason}`);
    setRedirectHistory((prev) => [...prev, location.pathname.toLowerCase()].slice(-3));
    navigate(path, { replace: true });
    setHasRedirected(true);
    hasEvaluatedRef.current = true;
  };

  const evaluateRedirect = () => {
    if (hasEvaluatedRef.current) {
      console.log('WithAuthentication: Redirect already evaluated, skipping');
      return;
    }

    const isLoggedIn = !!userStore.user;
    const emailVerified = userStore.user?.emailVerified || false;
    const isVerified = userStore.verified === true || userStore.verified === "true" || userStore.verified === "Verified";
    const isAdmin = userStore.userRole?.toLowerCase() === UserRole.ADMIN.toLowerCase();
    const isOnProtectedPath = location.pathname.toLowerCase().startsWith('/admin');
    const isOnVerification = location.pathname.toLowerCase().includes('/verification');
    const isOnIniti = location.pathname.toLowerCase().includes('/initi');
    const isOnIntro = location.pathname.toLowerCase() === '/home/intro';
    const isOnReferral = location.pathname.toLowerCase() === '/home/referral';
    const isOnProfile = location.pathname.toLowerCase() === '/home/profile';
    const currentPath = location.pathname.toLowerCase();

    console.log('Evaluating redirect -', {
      isLoggedIn, emailVerified, isVerified, isAdmin, isPublicPath,
      isOnProtectedPath, isOnVerification, isOnIniti, isOnIntro, isOnReferral, isOnProfile,
      userRole: userStore.userRole, pathname: currentPath,
      redirectHistory,
    });

    if (redirectHistory.includes(currentPath) && redirectHistory.length > 2) {
      console.warn('Redirect loop detected, aborting redirect:', currentPath);
      hasEvaluatedRef.current = true;
      return;
    }

    // Skip redirect logic for public paths
    if (isPublicPath) {
      console.log('WithAuthentication: Skipping redirect for public path:', currentPath);
      hasEvaluatedRef.current = true;
      return;
    }

    if (!hasRedirected && userStore.isLoaded && isInitialSyncDone) {
      if (!isLoggedIn) {
        handleRedirect('/login', 'User not logged in');
      } else if (!emailVerified && !isOnVerification && !isOnIniti && !isOnReferral && !isOnProfile) {
        handleRedirect('/verification', 'Email not verified');
      } else if (!isVerified && !isOnVerification && !isOnIniti && !isOnIntro && !isAdmin) {
        handleRedirect('/verification', 'User not verified (non-admin), allowing /profile and /referral');
      } else if (isOnProtectedPath && !isAdmin) {
        handleRedirect('/home/ols', 'Non-admin on admin path');
      } else if (authCondition && userStore.user && !authCondition(userStore.user)) {
        handleRedirect('/home/ols', 'Auth condition not met');
      } else if (isAdmin && !isOnProtectedPath && !isOnVerification) {
        handleRedirect('/admin/users/all', 'Admin on non-admin path');
      }
    } else {
      console.log('WithAuthentication: Skipping redirect -', {
        hasRedirected, isLoaded: userStore.isLoaded, isInitialSyncDone
      });
    }
    hasEvaluatedRef.current = true;
  };

  useEffect(() => {
    setHasRedirected(false);
    hasEvaluatedRef.current = false; // Reset on path change
    evaluateRedirect();
  }, [userStore.user, userStore.userRole, userStore.isLoaded, isInitialSyncDone, location.pathname, isPublicPath]);

  if (!userStore.isLoaded || !isInitialSyncDone) {
    console.log('WithAuthentication: Waiting for userStore or initial sync');
    return <Spin fullscreen />;
  }

  return <>{children}</>;
};

export default WithAuthentication;